import { apiClient } from '@/lib/api-client';

export type BookingRefusedReason =
  | 'gymPayment_date_passed'
  | 'gymPayment_marked_expired'
  | 'payment_end_date_passed'
  | 'payment_expired_flag'
  | 'no_payment_link';

export interface BookingRefusedRow {
  bookingId: string;
  date: string;
  reason: BookingRefusedReason;
  fullName: string;
  userId: string | null;
  classId: string;
  paymentId: string | null;
  planEndDate: string | null;
  planStartDate: string | null;
  paymentExpired: boolean;
  paymentExpiredReason: string | null;
  gpFound: boolean;
  gpExpiresAt: string | null;
  gpIsExpired: boolean;
  gpRemainingEntries: number | null;
  createdAt: string | null;
}

export interface BookingsPastPlanReport {
  today: string;
  counts: {
    total: number;
    future: number;
    past: number;
    byReason: Record<BookingRefusedReason, number>;
  };
  future: BookingRefusedRow[];
  past: BookingRefusedRow[];
}

// Back-compat alias
export type BookingPastPlanRow = BookingRefusedRow;

export const getBookingsPastPlan = async (): Promise<BookingsPastPlanReport> => {
  const response = await apiClient.get('/reports/bookings-past-plan');
  return response.data as BookingsPastPlanReport;
};

export type UserAccessExpiredReason =
  | 'only_legacy_expired'
  | 'all_gym_payments_expired'
  | 'legacy_and_gym_payments_expired';

export interface UserAccessExpiredRow {
  userId: string;
  name: string;
  surname: string;
  email: string | null;
  phone: string | null;
  userType: string;
  reason: UserAccessExpiredReason;
  hasActivePayment: boolean;
  legacyEndDate: string | null;
  legacyPaymentExpired: boolean;
  legacyPaymentExpiredReason: string | null;
  totalGymPayments: number;
  validGymPayments: number;
  lastGpExpiresAt: string | null;
  gpFixedCount: number;
  gpFixedEntriesRemaining: number;
  gpNonFixedCount: number;
  legacyIsFixed: boolean;
  legacyRemainingEntries: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UserMultiPlanRow {
  userId: string;
  name: string;
  surname: string;
  email: string | null;
  phone: string | null;
  legacyEndDate: string | null;
  totalGymPayments: number;
  validGymPayments: number;
  latestGpExpiresAt: string | null;
  gpFixedCount: number;
  gpFixedEntriesRemaining: number;
  gpNonFixedCount: number;
  legacyIsFixed: boolean;
  legacyRemainingEntries: number | null;
  createdAt: string | null;
}

export interface UsersPlanAnomaliesReport {
  today: string;
  noValidPlan: {
    total: number;
    byReason: Record<UserAccessExpiredReason, number>;
    users: UserAccessExpiredRow[];
  };
  multiplePlans: {
    total: number;
    users: UserMultiPlanRow[];
  };
}

export const getUsersPlanAnomalies =
  async (): Promise<UsersPlanAnomaliesReport> => {
    const response = await apiClient.get('/reports/users-plan-anomalies');
    return response.data as UsersPlanAnomaliesReport;
  };
